


















































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import VuePlotly from "@statnett/vue-plotly";
import QueryEndpointData from "./dataquery-mixin";
import { DensityType } from "@/store/datasets/types";

import { mixins } from "vue-class-component";

const MODE_COLORS: { [mode: string]: string } = {
    road: "#4E79A7", // steel blue
    rail: "#E15759", // warm red
    sea: "#76B7B2", // teal
    air: "#B07AA1", // purple
};
const FALLBACK_COLOR = "#8C8C8C";

@Component({
    components: {
        VuePlotly,
    },
})
export default class ModeShareBarChart extends mixins(QueryEndpointData) {
    @Prop() selectedMetric!: any;
    @Prop() selectedMetricTitle!: string;
    @Prop() map_id!: string;
    // Compact rendering for the inline SCL histogram tile: no header, no
    // legend, shorter labels and reduced height/margins so the chart fits in
    // the 370px-ish slot under the histogram without pushing it upward.
    @Prop({ default: false }) compact!: boolean;
    /** Optional chart width in pixels. Overrides Plotly's automatic
     *  sizing when container dimensions cannot be detected correctly
     *  (e.g. the inline `hist_supply_chain` view).
     *  Set to `null` to use the default auto-sizing behaviour.
     */
    @Prop({ default: null }) chartWidth!: number | null;
    /** Override for Plotly's `showlegend`. `null` (default) keeps the
     *  existing behaviour (`!compact` — modal "both" shows, compact
     *  variants hide). Pass `false` to force-hide the legend even on
     *  non-compact variants — used by the top-KPI modal histogram so
     *  the histogram's mode legend isn't duplicated by this chart. */
    @Prop({ default: null }) showLegend!: boolean | null;
    // Which rows to render:
    //   "both"    – metric share on top + tonnes share below (modal default)
    //   "metric"  – metric row only (used inline under the histogram)
    //   "tonnes"  – tonnes row only (used inline under the border-crossings
    //               chart, independent of the histogram open state)
    @Prop({ default: "both" }) rows!: "both" | "metric" | "tonnes";

    /** True when the tonnes-only variant has nothing to render under the
     *  current filters. */
    get hideForEmpty(): boolean {
        return this.rows === "tonnes" && this.filteredRows.length === 0;
    }

    /** Metric-row chart shown under each per-leg histogram. */
    get isLegMetricVariant(): boolean {
        return (
            this.rows === "metric" &&
            this.metric_type === "supply_chain_leg_cat"
        );
    }

    get showMetricRow(): boolean {
        return this.rows === "both" || this.rows === "metric";
    }

    get showTonnesRow(): boolean {
        return this.rows === "both" || this.rows === "tonnes";
    }

    // Modes shown in the chart.
    get transportModes(): string[] {
        return Object.entries(DensityType)
            .filter(([k]) => !k.includes("Events"))
            .map(([, v]) => v.toLowerCase());
    }

    get modeshareUrl() {
        return (
            process.env.VUE_APP_BACKEND_BASE + "/supplychainexplorer/modeshare"
        );
    }

    get rowLookup() {
        return this.getModeRowLookup();
    }

    get isBorderCrossingMetric() {
        return (
            this.selectedMetric?.value === "state_territory_border_crossings"
        );
    }

    // True when border crossings is the selected metric AND at least one mode
    // present in the data has zero crossings under the current filters.
    get hasZeroBorderCrossings() {
        if (!this.isBorderCrossingMetric) return false;
        return this.transportModes.some((mode) => {
            const row = this.rowLookup.get(mode);
            return row && row.value === 0;
        });
    }

    mounted() {
        this.loadData(this.modeshareUrl);
    }

    @Watch("updateRequired")
    reloadData(newVal: any, oldVal: any) {
        // Reload the data
        this.loadData(this.modeshareUrl);
    }

    get filteredRows(): any[] {
        if (!this.data || this.data.length === 0) return [];

        // Tonnes-only variant: the tonnes share by mode is invariant across
        // metrics for a given supply chain leg, so we ignore the metric
        // filter entirely and just take one row per mode for the matching
        // SCL.
        if (this.rows === "tonnes") {
            const seen = new Set<string>();
            const out: any[] = [];
            for (const row of this.data) {
                if (
                    this.metric_type !== "total" &&
                    row.supply_chain_leg_cat !== this.map_id
                ) {
                    continue;
                }
                if (seen.has(row.mode)) continue;
                seen.add(row.mode);
                out.push(row);
            }
            return out;
        }

        // Metric-row / both-row variants: still need a chosen metric so we
        // know which slice of the response to render.
        if (!this.selectedMetric) return [];

        if (this.metric_type === "total") {
            return this.data.filter(
                (row: any) => row.metric === this.selectedMetric.value,
            );
        }

        return this.data.filter(
            (row: any) =>
                row.supply_chain_leg_cat === this.map_id &&
                row.metric === this.selectedMetric.value,
        );
    }

    getModeColor(mode: string): string {
        return MODE_COLORS[mode.toLowerCase()] || FALLBACK_COLOR;
    }

    getModeRowLookup() {
        return new Map(this.filteredRows.map((r) => [r.mode, r]));
    }

    formatPercent(v: number) {
        if (v === 100) return "100";
        if (v >= 99.9 && v < 100) return "99.9";
        if (v >= 10) return v.toFixed(1);
        if (v >= 1) return v.toFixed(1);
        if (v >= 0.1) return v.toFixed(2);
        return v.toFixed(3);
    }

    // Y-axis category labels. Y-axis tick labels are hidden in every
    // reachable variant.
    get tonnesRowLabel(): string {
        return "Mode share of total<br>tonnes moved";
    }

    get metricRowLabel(): string {
        const rawTitle = this.selectedMetricTitle || "selected metric";
        const title = rawTitle.replace(/^(Avg|Total)\s+/i, "");
        const unit = this.selectedMetric?.unit
            ? ` ${this.selectedMetric.unit}`
            : "";
        return `Mode share of<br>${title}${unit}`;
    }

    // Y-axis categories in render order (bottom → top per Plotly default).
    // When only one row is rendered, the array shrinks to a single entry.
    get yCategories(): string[] {
        const cats: string[] = [];
        if (this.showTonnesRow) cats.push(this.tonnesRowLabel);
        if (this.showMetricRow) cats.push(this.metricRowLabel);
        return cats;
    }

    /** One horizontal stacked-bar trace per transport mode. Single-
     *  row variants only — modal `rows="both"` renders through
     *  `metricOnlyChartData` / `tonnesOnlyChartData` instead. */
    get chartData() {
        if (this.filteredRows.length === 0) return [];

        const yCats = this.yCategories;

        return this.transportModes
            .filter((mode) => this.rowLookup.has(mode))
            .map((mode) => {
                const row = this.rowLookup.get(mode);
                // `|| 0` (not `?? 0`) — `??` lets NaN through. Plotly
                // silently drops NaN x-values (the bar disappears with
                // no error), so we force NaN → 0 to keep the trace valid.
                const metricPct = (row.value || 0) * 100;
                const tonnesPct = (row.proportion_of_base || 0) * 100;
                const color = this.getModeColor(mode);
                const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1);

                const tonnesHover =
                    `<b>${modeLabel}</b><br>` +
                    `${this.formatPercent(tonnesPct)}% of total tonnes moved`;
                const metricHover =
                    this.isBorderCrossingMetric && metricPct === 0
                        ? `<b>${modeLabel}</b><br>No border crossings under current filters`
                        : `<b>${modeLabel}</b><br>` +
                          `${this.formatPercent(metricPct)}% of ` +
                          `${this.selectedMetricTitle}`;

                // Build x / text / hover arrays aligned with `yCats` order.
                const x: number[] = [];
                const text: string[] = [];
                const hovertext: string[] = [];
                if (this.showTonnesRow) {
                    x.push(tonnesPct);
                    text.push(
                        tonnesPct > 0
                            ? `${this.formatPercent(tonnesPct)}%`
                            : "",
                    );
                    hovertext.push(tonnesHover);
                }
                if (this.showMetricRow) {
                    x.push(metricPct);
                    text.push(
                        metricPct > 0
                            ? `${this.formatPercent(metricPct)}%`
                            : "",
                    );
                    hovertext.push(metricHover);
                }

                return {
                    name: modeLabel,
                    type: "bar",
                    orientation: "h",
                    x,
                    y: yCats,
                    marker: {
                        color,
                        opacity: 0.85,
                    },
                    text,
                    textposition: "inside",
                    insidetextanchor: "middle",
                    textfont: { color: "#ffffff", size: 14 },
                    hoverinfo: "text",
                    hovertext,
                    hovertemplate: "%{hovertext}<extra></extra>",
                };
            });
    }

    /** Builds one bar segment (one transport mode's slice) for one of
     *  the modal's two stacked mini-charts. Called once per mode per
     *  chart — four calls assemble each mini-chart's full stacked bar.
     *  `field` chooses which percentage to plot:
     *    - "value"               → metric share (% of selected metric)
     *    - "proportion_of_base"  → tonnes share (% of total tonnes) */
    buildSingleRowTrace(mode: string, field: "value" | "proportion_of_base") {
        const row = this.rowLookup.get(mode);
        const pct = (row[field] || 0) * 100;
        const color = this.getModeColor(mode);
        const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1);
        const yCat =
            field === "proportion_of_base"
                ? this.tonnesRowLabel
                : this.metricRowLabel;
        const hoverSuffix =
            field === "proportion_of_base"
                ? "% of total tonnes moved"
                : `% of ${this.selectedMetricTitle}`;
        const hovertext =
            this.isBorderCrossingMetric && field === "value" && pct === 0
                ? `<b>${modeLabel}</b><br>No border crossings under current filters`
                : `<b>${modeLabel}</b><br>${this.formatPercent(
                      pct,
                  )}${hoverSuffix}`;

        return {
            name: modeLabel,
            type: "bar",
            orientation: "h",
            x: [pct],
            y: [yCat],
            marker: { color, opacity: 0.85 },
            text: [pct > 0 ? `${this.formatPercent(pct)}%` : ""],
            textposition: "inside",
            insidetextanchor: "middle",
            textfont: { color: "#ffffff", size: 14 },
            cliponaxis: true,
            hoverinfo: "text",
            hovertext: [hovertext],
            hovertemplate: "%{hovertext}<extra></extra>",
        };
    }

    /** Chart data for the modal "both" variant's metric mini-chart
     *  (top bar). One trace per visible mode, each carrying the
     *  metric-share percentage. */
    get metricOnlyChartData(): any[] {
        if (this.filteredRows.length === 0) return [];
        return this.transportModes
            .filter((mode) => this.rowLookup.has(mode))
            .map((mode) => this.buildSingleRowTrace(mode, "value"));
    }

    /** Chart data for the modal "both" variant's tonnes mini-chart
     *  (bottom bar). One trace per visible mode, each carrying the
     *  tonnes-share percentage. */
    get tonnesOnlyChartData(): any[] {
        if (this.filteredRows.length === 0) return [];
        return this.transportModes
            .filter((mode) => this.rowLookup.has(mode))
            .map((mode) =>
                this.buildSingleRowTrace(mode, "proportion_of_base"),
            );
    }

    /** Per-segment "Road" / "Rail" / … annotations rendered above each
     *  modal mini-chart bar. Tiny segments (< 4 %) skip
     *  their label so it doesn't overflow. */
    modalRowAnnotations(field: "value" | "proportion_of_base"): any[] {
        const annotations: any[] = [];
        const MIN_SEGMENT_PCT_FOR_LABEL = 4;
        let cumulative = 0;
        for (const mode of this.transportModes) {
            if (!this.rowLookup.has(mode)) continue;
            const row = this.rowLookup.get(mode);
            const pct = (row[field] || 0) * 100;
            if (pct >= MIN_SEGMENT_PCT_FOR_LABEL) {
                const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1);
                annotations.push({
                    x: cumulative + pct / 2,
                    y: 0.63,
                    text: modeLabel,
                    showarrow: false,
                    font: { size: 12 },
                });
            }
            cumulative += pct;
        }
        return annotations;
    }

    /** Shared base layout for the modal "both" variant's mini-charts —
     *  single-row */
    get modalRowChartLayoutBase(): any {
        return {
            barmode: "stack",
            bargap: 0.15,
            margin: { l: 10, r: 10, t: 0, b: 0 },
            font: { size: 14 },
            xaxis: {
                range: [0, 100],
                fixedrange: true,
                showgrid: false,
                zeroline: false,
                showticklabels: false,
            },
            yaxis: {
                type: "category",
                fixedrange: true,
                showticklabels: false,
                automargin: false,
            },
            showlegend: false,
            paper_bgcolor: "#ffffff",
            plot_bgcolor: "#ffffff",
            hovermode: "closest",
            hoverlabel: { font: { size: 11 } },
            autosize: this.chartWidth == null,
            ...(this.chartWidth != null ? { width: this.chartWidth } : {}),
            height: 60,
            dragmode: false,
        };
    }

    /** Layout for the modal "both" variant's metric mini-chart —
     *  base layout + per-segment annotations driven by `value`
     *  (metric share). */
    get modalMetricRowLayout(): any {
        return {
            ...this.modalRowChartLayoutBase,
            annotations: this.modalRowAnnotations("value"),
        };
    }

    /** Layout for the modal "both" variant's tonnes mini-chart —
     *  base layout + per-segment annotations driven by
     *  `proportion_of_base` (tonnes share). */
    get modalTonnesRowLayout(): any {
        return {
            ...this.modalRowChartLayoutBase,
            annotations: this.modalRowAnnotations("proportion_of_base"),
        };
    }

    /** Annotation shown when the metric row would otherwise be empty (e.g.
     *  border-crossings metric with no crossings at all under current filters).
     *  Only applicable when the metric row is actually being rendered.
     */
    get emptyMetricRowAnnotation() {
        if (!this.showMetricRow) return null;
        if (this.filteredRows.length === 0) return null;
        const allZero = this.transportModes
            .filter((m) => this.rowLookup.has(m))
            .every((m) => this.rowLookup.get(m).value === 0);
        if (!allZero) return null;
        return {
            text: this.isBorderCrossingMetric
                ? "No border crossings under the current filters"
                : `No ${this.selectedMetricTitle} recorded under the current filters`,
            x: 50,
            y: this.metricRowLabel,
            xref: "x",
            yref: "y",
            showarrow: false,
            font: { size: 12, color: "#777" },
            bgcolor: "rgba(255,255,255,0.85)",
        };
    }

    /** The tonnes-share chart placed under each map (between the map and
     *  the BorderCrossings bar). Stays visible regardless of the histogram
     *  modal state. */
    get tonnesOnlyVariant(): boolean {
        return this.rows === "tonnes";
    }

    /** Per-segment mode labels ("Road", "Rail", …) rendered above
     *  each bar at y=0.63. Skips segments below `MIN_SEGMENT_PCT_FOR_LABEL` so a tiny
     *  slice doesn't get an overlapping label. */
    get topSegmentAnnotations(): any[] {
        const annotations: any[] = [];
        const MIN_SEGMENT_PCT_FOR_LABEL = 4;
        const pctField: "proportion_of_base" | "value" = this.tonnesOnlyVariant
            ? "proportion_of_base"
            : "value";
        let cumulative = 0;
        for (const mode of this.transportModes) {
            if (!this.rowLookup.has(mode)) continue;
            const row = this.rowLookup.get(mode);
            const pct = (row[pctField] || 0) * 100;
            if (pct >= MIN_SEGMENT_PCT_FOR_LABEL) {
                const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1);
                annotations.push({
                    x: cumulative + pct / 2,
                    y: 0.63,
                    text: modeLabel,
                    showarrow: false,
                    font: { size: 11 },
                });
            }
            cumulative += pct;
        }
        return annotations;
    }

    /** Caption text rendered below the bar for the two single-row
     *  compact placements (tonnes-only + leg-metric). For the metric
     *  variant, strips the leading "Avg "/"Total " */
    get topTitleText(): string {
        if (this.tonnesOnlyVariant) {
            return "Mode share of total tonnes moved";
        }
        if (this.isLegMetricVariant) {
            const rawTitle = this.selectedMetricTitle || "selected metric";
            const title = rawTitle.replace(/^(Avg|Total)\s+/i, "");
            const unit = this.selectedMetric?.unit
                ? ` ${this.selectedMetric.unit}`
                : "";
            return `Mode share of ${title}${unit}`;
        }
        return "";
    }

    /** Plain-text description string for the native browser `title`
     *  attribute on the chart's caption (tonnes-only + leg-metric
     *  variants). */
    get nativeTitleText(): string {
        if (this.tonnesOnlyVariant) {
            return (
                "Share of total tonnes moved by each transport mode " +
                "under the selected filters."
            );
        }
        if (this.isLegMetricVariant) {
            const rawMetric = this.selectedMetricTitle || "the selected metric";
            const plainMetric = rawMetric.replace(/<[^>]+>/g, "");
            let text =
                `The proportion of ${plainMetric} ` +
                "attributable to each transport mode under the selected " +
                "filters.";
            if (this.hasZeroBorderCrossings) {
                text +=
                    " If a mode has no segment, it means no state or " +
                    "territory border crossings occurred for that mode " +
                    "under the selected filters.";
            }
            return text;
        }
        return "";
    }

    /** Visible caption text below the metric mini-chart in the modal
     *  "both" variant. Renders with `v-html` so `<sub>` etc. in the
     *  metric title display correctly (e.g. CO₂). Strips the
     *  "Avg "/"Total " prefix since the modal already establishes
     *  the aggregation. */
    get modalMetricRowCaption(): string {
        const rawTitle = this.selectedMetricTitle || "selected metric";
        const title = rawTitle.replace(/^(Avg|Total)\s+/i, "");
        const unit = this.selectedMetric?.unit
            ? ` ${this.selectedMetric.unit}`
            : "";
        return `Mode share of ${title}${unit}`;
    }

    /** Native browser `title=` text for the modal "both" variant's
     *  metric-row caption. Describes what the top bar shows
     *  (mode share of the selected metric). */
    get metricLabelHover(): string {
        const rawMetric = this.selectedMetricTitle || "the selected metric";
        const plainMetric = rawMetric.replace(/<[^>]+>/g, "");
        let text =
            `Share of ${plainMetric}: the proportion of ${plainMetric} ` +
            "attributable to each transport mode (road / rail) under the " +
            "selected filters.";
        if (this.hasZeroBorderCrossings) {
            text +=
                " If a mode has no segment here but appears on the bottom " +
                "row, it means no state or territory border crossings " +
                "occurred for that mode under the selected filters.";
        }
        return text;
    }

    /** Native browser `title=` text for the modal "both" variant's
     *  tonnes-row. */
    get tonnesLabelHover(): string {
        return (
            "Share of total tonnes moved: the proportion of total tonnes " +
            "moved by each transport mode (road / rail) under the selected " +
            "filters. Provides context on the relative impact of the metric " +
            "values shown in the row above."
        );
    }

    // Canvas height for the single-row caption-below-bar layout.
    get chartHeight(): number {
        return 60;
    }

    // Minimal canvas padding — the caption is a separate DOM element
    // below this canvas (not a Plotly title), and the y-axis tick
    // labels are hidden, so the bar can sit flush against all four
    // edges.
    get layoutMargin() {
        return { l: 10, r: 10, t: 0, b: 0 };
    }

    get layout() {
        const annotations = [];
        if (this.emptyMetricRowAnnotation) {
            annotations.push(this.emptyMetricRowAnnotation);
        }

        // Per-segment mode labels ("Road", "Rail", …) at y=0.63 for
        // the top-title variants.
        const segmentAnnotations = this.topSegmentAnnotations;
        if (segmentAnnotations.length > 0) {
            annotations.push(...segmentAnnotations);
        } else {
            annotations.push({
                x: 50,
                y: 0.63,
                text: "0",
                font: { color: "rgba(0,0,0,0)" },
                showarrow: false,
            });
        }

        return {
            barmode: "stack",
            bargap: 0.15,
            margin: this.layoutMargin,
            font: { size: 14 },
            xaxis: {
                range: [0, 100],
                ticksuffix: "",
                fixedrange: true,
                showgrid: false,
                gridcolor: "#eee",
                zeroline: false,
                showticklabels: false,
            },
            yaxis: {
                type: "category",
                categoryorder: "array",
                categoryarray: this.yCategories,
                fixedrange: true,
                showticklabels: false,
                automargin: false,
            },
            // `showLegend` defaults to `!compact` (hidden in the
            // compact placements where the 3 mode colours are
            // consistent and per-segment hover surfaces mode names).
            // Pass the prop explicitly to override.
            showlegend:
                this.showLegend !== null ? this.showLegend : !this.compact,
            legend: {
                orientation: "h",
                x: 0.5,
                xanchor: "center",
                y: -0.15,
                yanchor: "top",
                font: { size: 11 },
            },

            annotations,

            paper_bgcolor: "#ffffff",
            plot_bgcolor: "#ffffff",
            hovermode: "closest",
            hoverlabel: { font: { size: 11 } },
            // Default to autosize-to-parent; pass an explicit `chartWidth`
            // to override (the inline `hist_supply_chain` placement needs
            // this — vue-plotly's autosize ignores the wrapper's CSS in
            // that surrounding container and overshoots).
            ...(this.chartWidth != null
                ? { autosize: false, width: this.chartWidth }
                : { autosize: true }),
            height: this.chartHeight,
        };
    }

    get options() {
        return {
            displayModeBar: false,
            staticPlot: false,
            responsive: true,
        };
    }
}
